import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useCompanyProfile', 'company-data');
const PUBLIC_SITE_KEY = 'lendwise-main';

const httpsUrlSchema = z.string().url().refine((value) => value.startsWith('https://'));

const publicCompanyProfileSchema = z.object({
  company_name: z.string().trim().min(1),
  legal_name: z.string().trim().min(1),
  company_nmls_id: z.string().trim().min(1),
  state_licenses: z.array(z.object({
    state: z.string().trim().min(1),
    license_number: z.string().trim().min(1)
  })).min(1),
  headquarters_address: z.object({
    street: z.string().trim().min(1),
    suite: z.string().trim().min(1).optional(),
    city: z.string().trim().min(1),
    state: z.string().trim().min(1),
    zip: z.string().trim().min(1)
  }),
  main_phone: z.string().trim().min(1),
  main_email: z.string().trim().email(),
  website: httpsUrlSchema,
  logo_url: httpsUrlSchema.nullable(),
  equal_housing_lender: z.literal(true),
  default_disclaimers: z.array(z.string().trim().min(1)).min(1),
  compliance_ready: z.literal(true)
});

export type PublicCompanyProfile = z.infer<typeof publicCompanyProfileSchema>;

interface UseCompanyProfileReturn {
  companyProfile: PublicCompanyProfile | null;
  loading: boolean;
  error: Error | null;
  retry: () => void;
}

/**
 * Fetches the public-safe, fail-closed company compliance contract used by the
 * unauthenticated secure-upload routes. It never queries company_profiles
 * directly and never substitutes regulatory fields.
 *
 * @returns {UseCompanyProfileReturn} Company profile data, loading state, and error
 */
export function useCompanyProfile(): UseCompanyProfileReturn {
  const [companyProfile, setCompanyProfile] = useState<PublicCompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);

  const retry = useCallback(() => {
    setRequestVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        log.info('Fetching public secure-upload company profile');

        const { data, error: fetchError } = await supabase
          .rpc('get_public_secure_upload_company_profile', {
            p_site_key: PUBLIC_SITE_KEY
          });

        if (fetchError) {
          throw new Error(`Failed to fetch public company profile: ${fetchError.message}`);
        }

        if (!isMounted) {
          return;
        }

        const parsed = publicCompanyProfileSchema.safeParse(data);
        if (!parsed.success) {
          log.error('Public company profile failed compliance validation', {
            metadata: { issues: parsed.error.issues }
          });
          throw new Error('Company compliance information is unavailable');
        }

        log.info('Public company profile loaded successfully', {
          metadata: { companyName: parsed.data.company_name }
        });
        setCompanyProfile(parsed.data);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        const errorObj = err instanceof Error ? err : new Error('Unknown error fetching company profile');
        log.error('Failed to fetch company profile', {}, errorObj);
        setError(errorObj);
        setCompanyProfile(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCompanyProfile();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [requestVersion]);

  return {
    companyProfile,
    loading,
    error,
    retry
  };
}
